import React, { useEffect, useRef, useState } from 'react'
import * as Icons from 'react-feather'
import DataTable from 'react-data-table-component'
import { Link } from 'react-router-dom'
import axios from 'axios'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg'
import StatusGridDropdown from '../../shared/common-dropdown/StatusGridDropdown'
import { getStorageValues } from '../../shared/CommonFunctions'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { logAuditEvent } from '../../utils/auditLogger'
import HandoverRegisterModal from './HandoverRegisterModal'
import HandoverRegisterExpandedRow from './HandoverRegisterExpandedRow'
import HandoverRegisterModel from './models/HandoverRegisterModel'

function HandoverRegisterGrid(props) {
  const [actionType, setActionType] = useState()
  const [modalIsOpen, setIsOpen] = useState(false)
  const [handoverModel, setHandoverModel] = useState(new HandoverRegisterModel())
  const [filterText, setFilterText] = useState('')
  const [alert, setAlert] = useState(null)
  const [editableCell, setEditableCell] = useState({ rowId: null, column: null })
  const [handoverRows, setHandoverRows] = useState([])
  const [expandedRowId, setExpandedRowId] = useState(null)
  const expandedRef = useRef(null)
  const moduleName = props.moduleName || 'Handover Register'

  useEffect(() => {
    setHandoverRows(props.HandoverRegisterData || [])
  }, [props.HandoverRegisterData])

  const rows = handoverRows
    ? handoverRows.filter((item) => (
      (item.SalesNumber && item.SalesNumber.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.CustomerName && item.CustomerName.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.MobileNumber && item.MobileNumber.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.PaymentMode && item.PaymentMode.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.Status && item.Status.toLowerCase().includes(filterText.toLowerCase()))
    ))
    : []

  const openActionType = (row, name) => {
    logAuditEvent(moduleName, 'View/Email Button Click', JSON.stringify({
      Id: row?.HandoverRegisterId,
      Name: row?.CustomerName,
      Action: name
    }))
    setHandoverModel(row)
    setIsOpen(true)
    setActionType(name)
  }

  const closeModal = () => {
    setIsOpen(false)
    props.fetchHandoverRegisters()
  }

  const updateStatus = async (option, row) => {
    if (!option || !row) return

    try {
      const response = await axios.post(getApiBaseUrl() + '/handoverregister/updatestatus', {
        HandoverRegisterId: row.HandoverRegisterId,
        StatusId: option.value,
        UserId: getStorageValues('userId')
      })
      if (response.status === 200) {
        CreateAlert('Status updated successfully!!', 'Success', setAlert)
        logAuditEvent(moduleName, 'Status Update', JSON.stringify({
          Id: row.HandoverRegisterId,
          StatusId: option.value,
          Status: option.label
        }))
        props.fetchHandoverRegisters()
      }
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to update status', 'Error', setAlert)
    }
  }

  const columns = [
    {
      name: <div className="titlename"><span>Customer Sales#</span></div>,
      selector: row => row.SalesNumber,
      cell: (row) => (
        <span title={`${row.SalesNumber} (${row.CustomerName || '-'})`}>
          {row.SalesNumber} ({row.CustomerName || '-'})
        </span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Payment Mode</span></div>,
      selector: row => row.PaymentMode,
      cell: (row) => (
        <span title={row.PaymentMode}>{row.PaymentMode || '-'}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Customer Mobile</span></div>,
      selector: row => row.MobileNumber,
      cell: (row) => (
        <span title={row.MobileNumber}>{row.MobileNumber || '-'}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: (
        <div className="titlename">
          <span>Status</span>
        </div>
      ),
      selector: row => row.Status,
      cell: (row) => {
        const isEditing = editableCell.rowId === row.HandoverRegisterId && editableCell.column === 'Status'
        return isEditing ? (
          <StatusGridDropdown
            key={`status-${row.HandoverRegisterId}`}
            autoFocus={true}
            showLabel={false}
            selectedValue={row.StatusId}
            onChangeDropdown={(e) => {
              updateStatus(e, row)
              setEditableCell({ rowId: null, column: null })
            }}
            onBlur={() => setEditableCell({ rowId: null, column: null })}
            openMenuOnFocus={true}
            openMenuOnClick={true}
          />
        ) : (
          <div
            className="editable-cell-with-icon"
            onClick={() => setEditableCell({ rowId: row.HandoverRegisterId, column: 'Status' })}
            style={{ cursor: 'pointer', height: '100%', width: '100%' }}
          >
            <div className="cell-content">{row.Status}</div>
            <span className="edit-icon">
              <Icons.Edit size={12} color="#1261a0" />
            </span>
          </div>
        )
      },
      sortable: true,
      sortIcon: <SortArrow />
    }
  ]

  if (props.UserPermissions.ViewModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          <Link
            className="me-2 p-2"
            to=""
            onClick={() => {
              openActionType(row, 'View')
            }}
          >
            <Icons.Eye data-feather="eye" className="action-eye" />
          </Link>
          <Link
            className="me-2 p-2"
            to=""
            onClick={() => {
              openActionType(row, 'Email')
            }}
            title="Send Email/WhatsApp"
          >
            <Icons.Mail data-feather="mail" size="14" className="feather-mail" />
          </Link>
        </div>
      ),
      center: 'true',
      width: '150px'
    })
  }

  const handleRowExpandToggled = (expanded, row) => {
    setExpandedRowId((prev) => (prev === row.HandoverRegisterId ? null : row.HandoverRegisterId))
  }

  const isRowExpanded = (row) => row.HandoverRegisterId === expandedRowId

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="SalesNumber"
            sortIcon={<SortArrow />}
            persistTableHead={true}
            expandableRows
            onRowExpandToggled={handleRowExpandToggled}
            expandableRowExpanded={isRowExpanded}
            expandableRowsComponent={HandoverRegisterExpandedRow}
            expandableRowsComponentProps={{ ref: expandedRef }}
          />
        </div>
      </div>
      <HandoverRegisterModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={handoverModel}
        moduleName={moduleName}
      />
    </>
  )
}

export default HandoverRegisterGrid
